import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  X, Save, Download, Copy, Check, Loader2, AlertCircle,
  ZoomIn, ZoomOut, RotateCcw, FileWarning, Code2, Archive,
} from 'lucide-react'
import { get, put, post, getBaseUrl } from '../utils/api'
import { toast } from 'sonner'
import ExtractDialog from './ExtractDialog'

const IMAGE_EXTS   = new Set(['jpg','jpeg','png','gif','webp','svg','ico','bmp','tiff','avif'])
const VIDEO_EXTS   = new Set(['mp4','webm','ogg','mov','avi'])
const AUDIO_EXTS   = new Set(['mp3','wav','ogg','flac','aac'])
const BINARY_EXTS  = new Set(['zip','tar','gz','bz2','rar','7z','pdf','exe','bin','iso','dmg'])
const ARCHIVE_EXTS = new Set(['zip','tar','gz','tgz','bz2'])

function isExtractable(filename) {
  const lower = (filename ?? '').toLowerCase()
  return lower.endsWith('.zip') || lower.endsWith('.tar.gz') || lower.endsWith('.tgz')
    || lower.endsWith('.tar.bz2') || lower.endsWith('.tar')
}

const LANG_MAP = {
  php:'php', js:'javascript', mjs:'javascript', jsx:'javascript',
  ts:'typescript', tsx:'typescript', css:'css', scss:'css',
  html:'html', htm:'html', xml:'xml', json:'json', yaml:'yaml', yml:'yaml',
  sh:'bash', bash:'bash', py:'python', sql:'sql', md:'markdown',
  toml:'toml', ini:'ini', conf:'nginx', env:'bash',
}

function ext(name) { return name?.split('.').pop()?.toLowerCase() ?? '' }

function getMime(name, apiMime) {
  const e = ext(name)
  if (IMAGE_EXTS.has(e)) return apiMime ?? `image/${e === 'jpg' ? 'jpeg' : e}`
  if (e === 'pdf')        return 'application/pdf'
  if (e === 'svg')        return 'image/svg+xml'
  return apiMime ?? 'text/plain'
}

/* ─── Code view with line numbers ─── */
function CodeView({ content, filename }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const lines = content.split('\n')

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{filename}</span>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="flex-1 overflow-auto bg-gray-900">
        <table className="w-full text-[12.5px] font-mono">
          <tbody>
            {lines.map((line, i) => (
              <tr key={i} className="hover:bg-white/[0.02] group">
                <td className="select-none text-right pr-4 pl-3 text-white/20 w-10 text-[11px] leading-5 group-hover:text-white/30">
                  {i + 1}
                </td>
                <td className="pl-0 pr-6 leading-5 text-gray-300 whitespace-pre">
                  {line || ' '}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ImageView({ src, filename, zoom, onZoom }) {
  const adjust = (d) => onZoom(z => Math.min(4, Math.max(0.1, +(z + d).toFixed(2))))

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{filename}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => adjust(-0.1)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"><ZoomOut size={13} /></button>
          <span className="text-xs text-gray-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => adjust(0.1)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"><ZoomIn size={13} /></button>
          <button type="button" onClick={() => onZoom(1)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"><RotateCcw size={13} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-auto flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900/50">
        <img
          src={src} alt={filename}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center', maxWidth: '100%', maxHeight: '100%', transition: 'transform 0.15s' }}
        />
      </div>
    </div>
  )
}

function PdfView({ src, filename }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{filename}</span>
      </div>
      <div className="flex-1 overflow-hidden">
        <iframe src={src} title={filename} className="w-full h-full border-0" />
      </div>
    </div>
  )
}

function VideoView({ src, mime, filename }) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 shrink-0">
        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{filename}</span>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900/30">
        <video controls className="max-w-full max-h-full rounded-xl shadow-xl">
          <source src={src} type={mime} />
        </video>
      </div>
    </div>
  )
}

function AudioView({ src, mime, filename }) {
  return (
    <div className="flex flex-col h-full items-center justify-center gap-6 p-8 bg-gray-50 dark:bg-gray-900/30">
      <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center border border-gray-200 dark:border-gray-600">
        <Code2 size={32} className="text-gray-400" />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{filename}</p>
      <audio controls className="w-full max-w-sm">
        <source src={src} type={mime} />
      </audio>
    </div>
  )
}

/* ─── Main FileViewer ─── */
export default function FileViewer({ domain, file, onClose, onExtracted }) {
  const [state, setState]         = useState('loading')
  const [fileData, setFileData]   = useState(null)
  const [editContent, setEditContent] = useState('')
  const [editMode, setEditMode]   = useState(false)
  const [dirty, setDirty]         = useState(false)
  const [saving, setSaving]       = useState(false)
  const [copied, setCopied]       = useState(false)
  const [imgZoom, setImgZoom]     = useState(1)
  const [showExtract, setShowExtract] = useState(false)
  const textareaRef  = useRef(null)
  const origContent  = useRef('')   // decoded content at load — used for cancel

  const name    = file?.name ?? ''
  const e       = ext(name)
  const lang    = LANG_MAP[e] ?? ''
  const isImage = IMAGE_EXTS.has(e)
  const isVideo = VIDEO_EXTS.has(e)
  const isAudio = AUDIO_EXTS.has(e)
  const isPdf   = e === 'pdf'
  const isBinExt = BINARY_EXTS.has(e)

  const load = useCallback(async () => {
    setState('loading')
    setEditMode(false)
    setDirty(false)
    try {
      const r = await get(`/websites/${domain}/files/read`, { path: file.path })
      const data = r?.data ?? r
      setFileData(data)
      // Show as text for any file that isn't a media type or known binary extension
      const isTextFile = !isImage && !isVideo && !isAudio && !isPdf && !isBinExt
      if (isTextFile) {
        let content = data?.content ?? ''
        // Decode base64 if the API encoded it (e.g. log files returned as binary)
        if (content && (data?.is_binary || data?.encoding === 'base64')) {
          try { content = atob(content) } catch { /* keep raw on decode failure */ }
        }
        origContent.current = content
        setEditContent(content)
      }
      setState('ready')
    } catch (err) {
      console.error(err)
      setState('error')
    }
  }, [domain, file?.path])

  useEffect(() => { if (file) load() }, [file, load])

  const isTextFile = !isImage && !isVideo && !isAudio && !isPdf && !isBinExt

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (ev) => {
      const ctrl = ev.ctrlKey || ev.metaKey

      // Ctrl+S — save (edit mode) or enter edit mode (view mode)
      if (ctrl && ev.key === 's') {
        ev.preventDefault()
        if (!isTextFile || state !== 'ready') return
        if (editMode) { if (dirty && !saving) save() }
        else setEditMode(true)
        return
      }

      // F2 / Ctrl+E — enter edit mode
      if ((ev.key === 'F2' || (ctrl && ev.key === 'e')) && isTextFile && state === 'ready' && !editMode) {
        ev.preventDefault()
        setEditMode(true)
        return
      }

      // Escape — cancel edit → close viewer
      if (ev.key === 'Escape') {
        if (editMode) {
          ev.preventDefault()
          setEditMode(false)
          setEditContent(origContent.current)
          setDirty(false)
        } else {
          onClose()
        }
        return
      }

      // Ctrl+D — download
      if (ctrl && ev.key === 'd') {
        ev.preventDefault()
        download()
        return
      }

      // Ctrl+C — copy content (view mode only, no text selected)
      if (ctrl && ev.key === 'c' && !editMode && isTextFile && state === 'ready') {
        const sel = window.getSelection()
        if (!sel || sel.toString() === '') {
          ev.preventDefault()
          copyContent()
        }
        return
      }

      // Image zoom: + / - / 0
      if (isImage) {
        if (ev.key === '+' || ev.key === '=') { ev.preventDefault(); setImgZoom(z => Math.min(4, +(z + 0.1).toFixed(2))) }
        if (ev.key === '-')                    { ev.preventDefault(); setImgZoom(z => Math.max(0.1, +(z - 0.1).toFixed(2))) }
        if (ev.key === '0')                    { ev.preventDefault(); setImgZoom(1) }
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state, editMode, dirty, saving, isTextFile, isImage])

  const save = async () => {
    setSaving(true)
    try {
      await put(`/websites/${domain}/files/write`, { path: file.path, content: editContent })
      toast.success('File saved')
      setDirty(false)
      setEditMode(false)
    } catch {
      toast.error('Failed to save file')
    } finally {
      setSaving(false)
    }
  }

  const download = () => {
    const token = localStorage.getItem('fm_token') || localStorage.getItem('panel_jwt')
    const base  = getBaseUrl();
    const url   = `${base}/websites/${encodeURIComponent(domain)}/files/download?path=${encodeURIComponent(file.path)}&token=${token}`
    const a = document.createElement('a')
    a.href = url; a.download = name; a.click()
  }

  const extractSuggestedDest = () => {
    const parent = file.path.split('/').slice(0, -1).join('/') || '/'
    const folder = name.replace(/\.(tar\.gz|tar\.bz2|zip|tgz|tar)$/i, '')
    return `${parent === '/' ? '' : parent}/${folder}`
  }

  const doExtract = async (destination) => {
    try {
      const res = await post(`/websites/${domain}/files/extract`, { archive: file.path, destination })
      if (res.success) {
        toast.success(`Extracted to ${destination}`)
        setShowExtract(false)
        onExtracted?.(destination)
      } else {
        toast.error(res.error?.message || res.message || 'Extraction failed')
      }
    } catch (err) {
      toast.error(err?.error?.message || err?.message || 'Extraction failed')
    }
  }

  const copyContent = async () => {
    const text = fileData?.content ?? editContent
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!file) return null

  let mediaSrc = null
  if (fileData && (isImage || isVideo || isAudio || isPdf)) {
    const raw = fileData?.content ?? fileData
    if (raw && typeof raw === 'string') {
      const mime = getMime(name, fileData?.mime_type)
      mediaSrc = fileData?.is_binary
        ? `data:${mime};base64,${raw}`
        : `data:${mime};charset=utf-8,${encodeURIComponent(raw)}`
    }
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Top bar */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{name}</p>
          {fileData?.size != null && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500">{(fileData.size / 1024).toFixed(1)} KB</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {/* Edit / Save */}
          {!isBinExt && !isImage && !isVideo && !isAudio && !isPdf && state === 'ready' && (
            editMode ? (
              <>
                <button
                  type="button"
                  onClick={() => { setEditMode(false); setEditContent(origContent.current); setDirty(false) }}
                  className="px-2.5 py-1 text-xs rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >Cancel</button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !dirty}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40 text-white transition-colors"
                >
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                  Save
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditMode(true)}
                className="px-2.5 py-1 text-xs rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600"
              >Edit</button>
            )
          )}

          {/* Copy */}
          {!isBinExt && !isImage && !isVideo && !isAudio && state === 'ready' && (
            <button type="button" onClick={copyContent} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs transition-colors">
              {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}

          {/* Download */}
          <button type="button" onClick={download} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs transition-colors">
            <Download size={13} /> Download
          </button>

          {/* Close */}
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden relative">
        {state === 'loading' && (
          <div className="flex items-center justify-center h-full gap-2 text-gray-400 text-xs">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
            <AlertCircle size={28} />
            <p className="text-sm">Failed to load file</p>
            <button type="button" onClick={load} className="text-xs text-primary hover:text-primary/80">Retry</button>
          </div>
        )}

        {state === 'ready' && (
          <>
            {isImage && mediaSrc && <ImageView src={mediaSrc} filename={name} zoom={imgZoom} onZoom={setImgZoom} />}
            {isVideo && mediaSrc && <VideoView src={mediaSrc} mime={getMime(name, fileData?.mime_type)} filename={name} />}
            {isAudio && mediaSrc && <AudioView src={mediaSrc} mime={getMime(name, fileData?.mime_type)} filename={name} />}
            {isPdf && mediaSrc && <PdfView src={mediaSrc} filename={name} />}

            {isBinExt && !isImage && !isVideo && !isAudio && !isPdf && (
              <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <FileWarning size={28} className="text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">Binary file — not previewable</p>
                <div className="flex items-center gap-2">
                  {isExtractable(name) && (
                    <button
                      type="button"
                      onClick={() => setShowExtract(true)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white hover:bg-primary/90 text-sm transition-colors"
                    >
                      <Archive size={14} /> Extract…
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={download}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm transition-colors border border-gray-200 dark:border-gray-600"
                  >
                    <Download size={14} /> Download
                  </button>
                </div>
              </div>
            )}

            {!isImage && !isVideo && !isAudio && !isPdf && !isBinExt && (
              editMode ? (
                <div className="flex flex-col h-full">
                  {dirty && (
                    <div className="px-4 py-1.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700/50 text-amber-600 dark:text-amber-400 text-xs">
                      Unsaved changes
                    </div>
                  )}
                  <textarea
                    ref={textareaRef}
                    value={editContent}
                    onChange={e => { setEditContent(e.target.value); setDirty(true) }}
                    className="flex-1 bg-gray-900 text-gray-200 font-mono text-[12.5px] leading-5 p-4 outline-none resize-none"
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                  />
                </div>
              ) : (
                <CodeView content={editContent} language={lang} filename={name} />
              )
            )}
          </>
        )}
      </div>

      {showExtract && (
        <ExtractDialog
          archiveName={name}
          suggestedDest={extractSuggestedDest()}
          onExtract={doExtract}
          onClose={() => setShowExtract(false)}
        />
      )}
    </div>
  )
}
